import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import {
  type DecisionFlowValue,
  type DecisionKeywordSet,
  type DecisionKeywordsView,
} from "@/lib/types";

/** How a scripted call decides what the caller said — the fixed logic, and the word lists that are editable.
 *
 *  Both halves on one screen on purpose. The lists are the editable part and the smaller part; which question
 *  consults which list, how long the assistant waits and what happens when nothing matches decide just as much and
 *  are fixed in code. An admin looking at "which words mean yes in Hungarian" is really asking "what happens on a
 *  Hungarian call", and the empty list only reads as a problem next to the sentence explaining what it costs.
 *
 *  Admin-only, and it says so: one edit changes call handling for every practice at once, with no per-practice
 *  staging to try it on first.
 */
export const DecisionLogic = () => {
  const query = useQuery({
    queryKey: ["decision-keywords"],
    queryFn: api.listDecisionKeywords,
  });

  if (query.isPending) return <p className="text-gray-500">Načítám…</p>;
  if (query.isError)
    return <p className="text-red-600">Chyba: {(query.error as Error).message}</p>;

  const view = query.data as DecisionKeywordsView;
  const languages = [...new Set(view.sets.map((s) => s.language))].sort();

  return (
    <div className="space-y-8">
      <FlowExplainer view={view} />
      <KeywordTables view={view} languages={languages} />
    </div>
  );
};

const KIND_LABEL: Record<string, string> = {
  yes: "Souhlas",
  no: "Zápor",
  nothing_further: "„To je vše“",
};

const KIND_HELP: Record<string, string> = {
  yes: "Co volající řekne, když souhlasí nebo potvrzuje. Nejen „ano“ — lidé potvrzují i tím, že přitakají faktu.",
  no: "Jen skutečné zápory. Nic delšího ani idiomatického — tady je chybné slovo nejdražší.",
  nothing_further:
    "Jen pro otázku „Je to všechno?“. Prázdný seznam je bezpečný: nejasnou odpověď posoudí model. Sdílení tohoto seznamu s běžnými otázkami kdysi způsobilo, že „Všechno správně“ znamenalo zápor.",
};

const KIND_ORDER = ["yes", "no", "nothing_further"];

const FlowExplainer = ({ view }: { view: DecisionKeywordsView }) => (
  <section className="rounded border border-gray-200 bg-white p-6">
    <h3 className="mb-1 text-sm font-semibold text-[#1a2b6b]">
      Jak Emmy rozhoduje během hovoru
    </h3>
    <p className="mb-4 text-xs text-gray-500">
      Platí pro řízený scénář (V1). Tato část se nedá měnit z administrace — je
      v kódu agenta. Editovatelné jsou jen seznamy slov níže.
    </p>

    <ol className="space-y-3">
      {view.flow.steps.map((step) => (
        <li key={step.title} className="border-l-2 border-gray-200 pl-3">
          <p className="text-sm font-medium text-gray-800">
            {step.title}
            {step.editable && (
              <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                používá: {step.editable}
              </span>
            )}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-gray-600">
            {step.detail}
          </p>
        </li>
      ))}
    </ol>

    <div className="mt-6 grid gap-6 sm:grid-cols-3">
      <ValueList title="Časování" values={view.flow.timings} />
      <ValueList title="Stropy" values={view.flow.limits} />
      <ValueList title="Klávesnice" values={view.flow.keypad} />
    </div>
  </section>
);

const ValueList = ({
  title,
  values,
}: {
  title: string;
  values: DecisionFlowValue[];
}) => (
  <div>
    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
      {title}
    </h4>
    <dl className="space-y-2">
      {values.map((v) => (
        <div key={v.label}>
          <dt className="text-xs font-medium text-gray-700">
            {v.label} — <span className="text-[#1a2b6b]">{v.value}</span>
          </dt>
          <dd className="text-[11px] leading-snug text-gray-500">{v.detail}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const KeywordTables = ({
  view,
  languages,
}: {
  view: DecisionKeywordsView;
  languages: string[];
}) => (
  <section>
    <h3 className="mb-1 text-sm font-semibold text-[#1a2b6b]">
      Klíčová slova pro ano / ne
    </h3>
    <p className="mb-4 text-xs text-gray-500">
      Platí pro <strong>všechny ordinace najednou</strong> — nejde je nastavit
      per ordinace. Nejdřív se zkouší tato slova; co se netrefí, posoudí podle
      významu Gemini. Prázdný seznam tedy hovor nerozbije, jen každou odpověď
      v tom jazyce prodraží o jedno kolo navíc.
    </p>

    <div className="space-y-4">
      {languages.map((language) => (
        <LanguageCard
          key={language}
          language={language}
          sets={view.sets.filter((s) => s.language === language)}
          inUse={view.languagesInUse.includes(language)}
        />
      ))}
    </div>
  </section>
);

const LanguageCard = ({
  language,
  sets,
  inUse,
}: {
  language: string;
  sets: DecisionKeywordSet[];
  inUse: boolean;
}) => {
  const ordered = KIND_ORDER.map((k) => sets.find((s) => s.kind === k)).filter(
    (s): s is DecisionKeywordSet => s !== undefined,
  );
  const unreviewed = ordered.some((s) => !s.reviewed);
  // The empty cell is the valuable one, and only when somebody is actually offered the language.
  const emptyAndLive =
    inUse &&
    ordered.some((s) => s.kind !== "nothing_further" && s.tokens.length === 0);

  return (
    <div className="rounded border border-gray-200 bg-white p-5">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-[#1a2b6b]">
          {language.toUpperCase()}
        </h4>
        {inUse && (
          <span
            title="Některá ordinace tento jazyk nabízí volajícím."
            className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"
          >
            v provozu
          </span>
        )}
        {unreviewed && (
          <span
            title="Seznam nikdo, kdo jazyk umí, nezkontroloval. Byl vygenerován nebo napsán bez rodilého mluvčího."
            className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
          >
            nezkontrolováno
          </span>
        )}
      </div>

      {emptyAndLive && (
        <p className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Tento jazyk se volajícím nabízí, ale nemá klíčová slova. Každá odpověď
          ano/ne v něm je pro Emmy nejasná, takže o ní rozhoduje model — hovor
          funguje, jen o kousek pomaleji. Doplněním pár jistých slov to spravíte.
        </p>
      )}

      <div className="space-y-4">
        {ordered.map((set) => (
          <KeywordRow key={set.kind} set={set} />
        ))}
      </div>
    </div>
  );
};

/** One editable list. Comma-separated, because that is how somebody thinks about a handful of words — a tag widget
 *  would be more clicks to do the same thing and harder to paste into. */
const KeywordRow = ({ set }: { set: DecisionKeywordSet }) => {
  const qc = useQueryClient();
  const stored = set.tokens.join(", ");
  const [text, setText] = useState(stored);
  const [warnings, setWarnings] = useState<string[]>([]);
  useEffect(() => setText(stored), [stored]);

  const parse = (raw: string) =>
    raw
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

  const refresh = () => qc.invalidateQueries({ queryKey: ["decision-keywords"] });

  const save = useMutation({
    mutationFn: () => api.updateDecisionKeywords(set.language, set.kind, parse(text)),
    onSuccess: (res) => {
      setWarnings(res.warnings ?? []);
      refresh();
    },
  });

  const reset = useMutation({
    mutationFn: () => api.resetDecisionKeywords(set.language, set.kind),
    onSuccess: () => {
      setWarnings([]);
      refresh();
    },
  });

  const dirty = text !== stored;
  const customised =
    [...set.tokens].sort().join(" ") !==
    [...set.defaultTokens].sort().join(" ");

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="text-xs font-semibold text-gray-700">
            {KIND_LABEL[set.kind] ?? set.kind}
          </span>
          {customised && (
            <span
              title="Upraveno oproti tomu, co je zabudováno v agentovi."
              className="ml-2 text-[10px] uppercase text-gray-400"
            >
              upraveno
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {customised && (
            <button
              onClick={() => reset.mutate()}
              disabled={reset.isPending}
              className="text-[11px] text-gray-500 underline hover:text-gray-700 disabled:text-gray-300"
            >
              {reset.isPending ? "Vracím…" : "Vrátit na výchozí"}
            </button>
          )}
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="rounded bg-[#1a2b6b] px-2.5 py-1 text-[11px] font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {save.isPending ? "Ukládám…" : dirty ? "Uložit" : "Uloženo"}
          </button>
        </div>
      </div>

      <p className="mb-1 text-[11px] leading-snug text-gray-500">
        {KIND_HELP[set.kind]}
      </p>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="Oddělte čárkou. Prázdné pole je v pořádku — rozhodne model."
        className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-xs focus:border-[#1a2b6b] focus:outline-none"
      />

      {save.isError && (
        <p className="mt-1 text-[11px] text-red-600">
          {(save.error as Error).message}
        </p>
      )}
      {warnings.map((w) => (
        <p key={w} className="mt-1 text-[11px] text-amber-700">
          {w}
        </p>
      ))}
      {customised && (
        <p className="mt-1 text-[11px] text-gray-400">
          Výchozí: {set.defaultTokens.join(", ") || "(prázdné)"}
        </p>
      )}
    </div>
  );
};
